import type {
  BrandPalette,
  ComponentOverrides,
  ConstructorValue,
  Typography,
  FontFamily,
} from '../types';
import { FONT_FAMILIES } from '../types';

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function parsePalette(raw: unknown): BrandPalette | undefined {
  if (!isPlainObject(raw)) return undefined;
  const out: BrandPalette = {};
  const keys: (keyof BrandPalette)[] = [
    'primary', 'secondary', 'accent', 'neutral', 'success', 'danger',
    'surfaceBg', 'surfaceCard', 'surfaceBorder',
    'textPrimary', 'textMuted', 'textOnBrand',
    'heroFrom', 'heroTo',
    'pageBgFrom', 'pageBgTo',
  ];
  for (const key of keys) {
    const v = raw[key];
    if (typeof v === 'string' && v.length > 0) out[key] = v;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

function parseOverrides(raw: unknown): ComponentOverrides | undefined {
  if (!isPlainObject(raw)) return undefined;
  const out: ComponentOverrides = {};
  for (const [componentName, knobMap] of Object.entries(raw)) {
    if (!isPlainObject(knobMap)) continue;
    const cleanKnobs: Record<string, string | number> = {};
    for (const [knobName, value] of Object.entries(knobMap)) {
      if (typeof value === 'string' || typeof value === 'number') {
        cleanKnobs[knobName] = value;
      }
    }
    if (Object.keys(cleanKnobs).length > 0) out[componentName] = cleanKnobs;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

function isFontFamily(v: unknown): v is FontFamily {
  return typeof v === 'string' && (FONT_FAMILIES as readonly string[]).includes(v);
}

function parseTypography(raw: unknown): Typography | undefined {
  if (!isPlainObject(raw)) return undefined;
  const out: Typography = {};
  if (isFontFamily(raw.fontPrimary)) out.fontPrimary = raw.fontPrimary;
  if (isFontFamily(raw.fontSecondary)) out.fontSecondary = raw.fontSecondary;
  if (typeof raw.baseSize === 'number' && raw.baseSize > 0) out.baseSize = raw.baseSize;
  if (typeof raw.fontFamily === 'string' && raw.fontFamily.length > 0) {
    out.fontFamily = raw.fontFamily;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * Parse a v2 ConstructorValue from raw JSON. Returns null if `version !== 2`
 * or if input is not an object. Malformed fields are dropped silently.
 */
export function parseConstructorV2(raw: unknown): ConstructorValue | null {
  if (!isPlainObject(raw)) return null;
  if (raw.version !== 2) return null;
  const out: ConstructorValue = { version: 2 };
  const palette = parsePalette(raw.palette);
  if (palette) out.palette = palette;
  const overrides = parseOverrides(raw.overrides);
  if (overrides) out.overrides = overrides;
  const typography = parseTypography(raw.typography);
  if (typography) out.typography = typography;
  return out;
}

/**
 * Serialize ConstructorValue into the v2 backend JSON shape. Empty palette,
 * empty overrides, undefined fields are omitted. `version` always present.
 */
export function toBackendJson(v: ConstructorValue): Record<string, unknown> {
  const out: Record<string, unknown> = { version: v.version };
  if (v.palette && Object.keys(v.palette).length > 0) out.palette = v.palette;
  if (v.overrides && Object.keys(v.overrides).length > 0) out.overrides = v.overrides;
  if (v.typography && Object.keys(v.typography).length > 0) out.typography = v.typography;
  return out;
}
